import {
  Box,
  Container,
  Divider,
  InputLabel,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import FlowInfo from '../FlowInfo';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../redux';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { minMax } from '../../constants';
import NumberDropZone from '../../DropZones/NumberDropZone';
import {
  deleteField,
  resetSource,
  setSource,
  updateSourceAggregation,
} from '../../../../redux/Calculations/CalculationsActions';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import { useState } from 'react';
import InputField from '../../Conditions/Evaluation/InputField';
interface PowerProps {
  propKey: string;
  isFlowUsedInOtherFlowsAsSource: boolean;
}
function Power({ propKey, isFlowUsedInOtherFlowsAsSource }: PowerProps) {
  const calculations = useSelector((state: RootState) => state.calculations);
  const dispatch = useDispatch();
  const currCalculation = calculations.properties[propKey];

  const activeFlow = currCalculation?.currentCalculationSession?.activeFlow!;
  const calculationInfo = currCalculation?.currentCalculationSession?.calculationInfo!;
  const source = calculationInfo?.flows[activeFlow][0].source;
  const [powerValue, setPowerValue] = useState<string>(source?.[1] ?? 2);
  const sourceType = calculationInfo?.flows[activeFlow][0]!.sourceType;
  const min = minMax['Number']['Power'].min;
  const max = minMax['Number']['Power'].max;
  const getName = (): string | null => {
    if (!currCalculation || !currCalculation.currentCalculationSession) return null;
    if (sourceType[0] === 'field') return calculationInfo.fields[source[0]].displayName;
    else if (sourceType[0] === 'flow')
      return currCalculation.currentCalculationSession.flowDisplayNames[source[0]];
    return source[0];
  };
  const onAddCustomValue = (type: 'parameter' | 'static', datatype?: string) => {
    if (sourceType?.[0] === 'field') {
      dispatch(deleteField(propKey, source[0]));
    }
    if (type === 'static') {
      const value = datatype === 'integer' ? 2 : 2.0;
      dispatch(setSource(propKey, activeFlow, 0, value, datatype!, 0));
    }
  };

  const sourceListDownArrowOptions = [
    { displayName: 'sum', value: 'sum' },
    { displayName: 'avg', value: 'avg' },
    { displayName: 'min', value: 'min' },
    { displayName: 'max', value: 'max' },
    { displayName: 'count', value: 'count' },
    { displayName: 'count Non Null', value: 'countnn' },
    { displayName: 'count Null', value: 'countn' },
    { displayName: 'count Unique', value: 'countu' },
  ];
  const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text');
    if (!/^\d*\.?\d+$/.test(paste)) {
      e.preventDefault();
      return;
    }
    const num = Number(paste);
    if (isNaN(num)) return;

    if (num < 1 || num > 10) {
      e.preventDefault();
    }
    setPowerValue(String(num));
  };
  const isAggregated = calculationInfo?.flows[activeFlow][0]!.isAggregation;
  const aggregation = calculationInfo?.flows[activeFlow][0]!.aggregation[0] ?? 'sum';
  if (!activeFlow || !calculationInfo || !source || !sourceType) return null;
  return (
    <Container
      disableGutters
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        margin: 0,
      }}
      maxWidth="xs"
    >
      <FlowInfo
        propKey={propKey}
        flowname="Base"
        min={min}
        max={max}
        currentValue={source[0] ? 1 : 0}
        onAddCustomValue={onAddCustomValue}
      />
      <NumberDropZone
        propKey={propKey}
        flow={activeFlow}
        sx={{
          ...(![null, undefined].includes(source[0]) ? { minHeight: '5rem', flexGrow: 0 } : {}),
        }}
      >
        {source.length === 0 ? (
          <p
            style={{ fontSize: '11px', color: '#999999', paddingLeft: '8px', textAlign: 'center' }}
          >
            Drop a column here
          </p>
        ) : (
          <ListItem
            className="axisFilterField"
            sx={{
              borderRadius: '5px',
              marginBlock: '5px',
              marginLeft: '0.5rem',
              paddingLeft: '0.3rem',
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: '24px', // Match this height with TextField height if needed
              width: '95%',
              gap: '0.2rem',
            }}
          >
            {!isFlowUsedInOtherFlowsAsSource && (
              <button
                type="button"
                className="buttonCommon columnClose"
                onClick={() => {
                  dispatch(resetSource(propKey, activeFlow, 0));
                }}
                title="Remove field"
              >
                <CloseRoundedIcon style={{ fontSize: '13px' }} />
              </button>
            )}
            {isFlowUsedInOtherFlowsAsSource && (
              <Tooltip
                title="This calculation is being used in another flow. Please remove the dependency before deleting."
                slotProps={{
                  tooltip: {
                    sx: {
                      fontSize: '12px',
                    },
                  },
                }}
              >
                <LockOutlinedIcon
                  style={{
                    color: '#2BB9BB',
                  }}
                />
              </Tooltip>
            )}
            {['field', 'flow'].includes(sourceType[0]) ? (
              <ListItemText
                primary={getName()}
                sx={{ '& .MuiListItemText-primary': { fontSize: '11px' } }}
              />
            ) : (
              <InputField
                type="number"
                isDecimal={sourceType[1] === 'decimal'}
                value={String(source[0])}
                onBlur={(value) => {
                  const datatype = sourceType[1] === 'decimal' ? 'decimal' : 'integer';
                  dispatch(setSource(propKey, activeFlow, 0, value, datatype, 0));
                }}
                disabled={isFlowUsedInOtherFlowsAsSource}
              />
            )}

            {sourceType[0] === 'field' && (
              <div
                style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}
              >
                {isAggregated && (
                  <span style={{ userSelect: 'none', marginRight: '8px' }}>
                    {isAggregated ? aggregation : ''}
                  </span>
                )}
                {isAggregated && !isFlowUsedInOtherFlowsAsSource && (
                  <Select
                    size="small"
                    sx={{
                      backgroundColor: 'white',
                      cursor: 'auto',
                      border: 'none',
                      outline: 'none',
                      '.MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                      boxShadow: 'none',
                      padding: '0',
                      '.MuiSelect-select': {
                        padding: '2px 28px',
                      },
                      '.MuiSelect-select.MuiInputBase-input': {
                        paddingRight: '0',
                      },
                    }}
                  >
                    {sourceListDownArrowOptions.map((item, id) => {
                      return (
                        <MenuItem
                          sx={{
                            listStyle: 'none',
                            cursor: 'pointer',
                            color: 'black',
                            backgroundColor:
                              aggregation === item.displayName
                                ? 'rgba(43, 185, 187, 0.5) !important'
                                : 'white !important',
                            '&:hover': {
                              backgroundColor: 'rgba(43, 185, 187, 0.3) !important',
                            },
                          }}
                          key={id}
                          title="Window Function"
                          onClick={() => {
                            dispatch(
                              updateSourceAggregation(propKey, activeFlow, 0, 0, item.value),
                            );
                          }}
                        >
                          {item.displayName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
              </div>
            )}
            {sourceType[0] === 'flow' && (
              <div style={{ alignItems: 'center', marginLeft: 'auto', flexShrink: 0 }}>
                {isAggregated && (
                  <span style={{ userSelect: 'none', marginLeft: '10px' }} className="columnPrefix">
                    Agg
                  </span>
                )}
              </div>
            )}
          </ListItem>
        )}
      </NumberDropZone>
      {source.length > 0 ? (
        <>
          <Divider />
          <Box
            sx={{
              cursor: 'pointer',
              textAlign: 'left',
              paddingBottom: '10px',
              minHeight: '4rem',
              marginTop: '0',
            }}
          >
            <InputLabel
              sx={{
                marginLeft: '0.5rem',
                marginTop: '0.5rem',
                fontSize: '12px',
                // color: "gray",
                fontWeight: 'bold',
              }}
            >
              Power
            </InputLabel>
            <TextField
              error={
                powerValue !== '' &&
                (isNaN(Number(powerValue)) || Number(powerValue) < 0 || Number(powerValue) > 10)
              }
              id="power-input"
              type="text"
              inputMode="numeric"
              value={powerValue}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '') {
                  setPowerValue(val);
                  return;
                }
                if (!/^\d*\.?\d*$/.test(val)) return;
                setPowerValue(val);
              }}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
                input: { style: { height: '25px' } },
                htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === '' || val === '.') {
                  setPowerValue('');
                  return;
                }
                let num = Number(val);

                if (isNaN(num)) return;
                if (num < 0) num = 0;
                if (num > 10) num = 10;

                const finalVal = String(num);
                setPowerValue(finalVal);
                const isDecimal = val.includes('.');

                dispatch(
                  setSource(propKey, activeFlow, 0, finalVal, isDecimal ? 'decimal' : 'integer', 1),
                );
              }}
              sx={{
                width: '95%',
                marginLeft: '0.5rem',
                '& .MuiInputBase-root': {
                  height: '2rem',
                  fontSize: '11px',
                },
                '& .MuiTextField-root': {
                  outline: 'none',
                  border: 'none',
                },
                border: 'none',
                marginTop: '0.5rem',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: '#2bb9bb',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '16.5px 14px', // Consistent padding for all browsers
                },
                // Firefox-specific styling
                '@supports (-moz-appearance: none)': {
                  '& .MuiOutlinedInput-input': {
                    padding: '16.5px 2px 16.5px 14px',
                  },
                },
              }}
              onPaste={handlePaste}
              disabled={isFlowUsedInOtherFlowsAsSource}
            />
          </Box>
        </>
      ) : null}
    </Container>
  );
}

export default Power;
